// Centralised product-title cleaning + uniqueness guards.
//
// Supplier feeds ship long machine-generated titles. We clean them, but we MUST
// preserve every attribute that distinguishes one SKU from another: colour,
// material, functional modifier, piece count, configuration and trailing
// "with …" feature. Two products visible on the same screen must never share a
// display name.

const VENDORS =
  /\b(HomeRoots|HomeSullivan|Furniture of America|Baxton Studio|Modway|Flash Furniture|FurniFindUSA|Furnifind|FurniFind)\b/gi;

// Colour / material vocabularies, the contradiction guard and the handle
// qualifier extractor live in scripts/title-qualifiers.mjs — ONE implementation,
// shared with the build-time head titles (round 36).
import {
  ANY_COLOR_RE,
  COLOR_PREFIXES,
  COLORS,
  LEADING_COLOR_RE,
  MATERIAL_RE,
  MATERIAL_WORDS,
  STYLE_DESCRIPTORS,
  extractSizeLabel,
  extractSingleMeasure,
  handleQualifiers,
  isUsableQualifier,
  titleCase,
} from "../../scripts/title-qualifiers.mjs";
// Round 37: chemical-nomenclature strip + noun-only stutter collapse, shared with
// the build-time title builder so display names and head titles agree.
import {
  collapseStutters,
  displayNameDefects,
  nounStem,
  productPhrase,
  stripChemicalNames,
  stripSpecBlock,
} from "../../scripts/product-meta.mjs";

// Multi-word product types matched first (longest first), then single-word.
const PRODUCT_TYPES: string[] = [
  "Sectional Sofa", "Sleeper Sofa", "Loveseat Sofa", "Convertible Sofa",
  "TV Stand", "Media Console", "Media Cabinet", "Media Center",
  "Console Table", "Coffee Table", "Side Table", "End Table", "Accent Table",
  "Dining Table", "Dining Chair", "Dining Bench", "Bar Cart", "Bar Stool", "Counter Stool",
  "Bar Chair", "Counter Chair",
  "Bed Frame", "Platform Bed", "Headboard", "Bunk Bed", "Daybed",
  "Arm Chair", "Armchair", "Accent Chair", "Lounge Chair", "Office Chair", "Swivel Chair", "Rocking Chair",
  "Filing Cabinet", "Storage Cabinet", "Display Cabinet", "Curio Cabinet", "Medicine Cabinet", "Linen Cabinet",
  "Shoe Rack", "Coat Rack", "Wine Rack",
  "Ottoman Bench", "Storage Ottoman", "Storage Bench",
  "Floor Lamp", "Table Lamp", "Pendant Light", "Wall Sconce",
  "Area Rug", "Rug Pad", "Throw Pillow", "Wall Mirror", "Floor Mirror",
  "Lamp Shade", "Bathroom Vanity",
  "Patio Set", "Outdoor Sofa", "Outdoor Chair",
  // Single words last
  "Sectional", "Sofa", "Loveseat", "Couch", "Chaise", "Settee",
  "Sideboard", "Buffet", "Credenza", "Hutch", "Vanity",
  "Bookcase", "Bookshelf", "Shelf", "Shelving", "Etagere",
  "Dresser", "Nightstand", "Wardrobe", "Armoire", "Chest",
  "Desk", "Bench", "Ottoman", "Stool", "Chair",
  "Bed", "Mattress", "Mirror", "Lamp", "Rug", "Pillow", "Cushion", "Cabinet", "Table",

  // Lighting & decor head nouns
  "Chandelier", "Sconce", "Pendant", "Lantern", "Lampshade",
  // Tabletop objects whose head noun would otherwise collapse to "Table"
  "Table Clock", "Wall Clock", "Mantel Clock", "Clock",
  "Sculpture", "Vase", "Bowl", "Tray", "Candle Holder", "Planter",
  "Doormat", "Throw", "Blanket", "Frame",

];


// Lighting / decor descriptors that genuinely distinguish one piece from
// another: shade shapes, forms and placement.
const LIGHT_DESCRIPTORS: Array<[RegExp, string]> = [
  [/\bTorchiere\b/i, "Torchiere"],
  [/\bBedside\b/i, "Bedside"],
  [/\bBuffet\b/i, "Buffet"],
  [/\bDesk\b/i, "Desk"],
  [/\bTripod\b/i, "Tripod"],
  [/\bCaged\b/i, "Caged"],
  [/\bLantern\b/i, "Lantern"],
  [/\bStarburst\b/i, "Starburst"],
  [/\bArc\b/i, "Arc"],
  [/\bDrum\b/i, "Drum"],
  [/\bEmpire\b/i, "Empire"],
  [/\bBowl\b/i, "Bowl"],
  [/\bCone\b/i, "Cone"],
  [/\bDome\b/i, "Dome"],
  [/\bRectangular\b/i, "Rectangular"],
];

// A material with the word that changes what it is: "Silk Blend", "Leather Match".
const MATERIAL_BLEND_RE = new RegExp(`${MATERIAL_RE.source}(?:\\s+(Blend|Match))?`, "i");
// The supplier stating the material outright: "100% Cotton" (group 1 stays empty: no prefix).
const PCT_MATERIAL_RE = new RegExp(`\\b()100%\\s+(${MATERIAL_WORDS})\\b`, "i");

// Functional modifiers — never dropped.
const FUNCTIONAL_RE =
  /\b(?:(Power|Manual)\s+)?(Reclining|Recliner|Sleeper|Swivel|Extending|Extendable|Convertible|Folding|Stackable|Adjustable|Outdoor)\b/i;


// Tech/feature words that are genuine selling points on lighting and decor.
// Treated like functional modifiers: preserved through recomposition, never
// added to a product whose raw title does not contain them.
const TECH_FEATURES: Array<[RegExp, string]> = [
  [/\bLED\b/i, "LED"],
  [/\bUSB\b/i, "USB"],
];

// Configuration words, highest priority first.
const CONFIGS: Array<[RegExp, string]> = [
  [/\bU[-\s]?Shaped\b/i, "U-Shaped"],
  [/\bL[-\s]?Shaped\b/i, "L-Shaped"],
  [/\bCurved\b/i, "Curved"],
  [/\bModular\b/i, "Modular"],
  [/\bChaise\b/i, "Chaise"],
  [/\bCorner\b/i, "Corner"],
];

const NUM_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
  seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
};

const ACRONYMS = new Set([
  "USB", "LED", "TV", "UV", "HDMI", "AC", "DC", "RGB", "IP", "BTU", "QI",
]);


const PLURAL_TYPES = new Set([
  "Dining Chair", "Bar Stool", "Counter Stool", "Bar Chair", "Counter Chair", "Armchair",
  "Accent Chair", "Lounge Chair", "Chair", "Stool",
  "Table Lamp", "Floor Lamp", "Lamp", "Vase", "Sculpture", "Frame", "Throw",
]);


// Primary seating/sleeping types. When any of these is present in a title it IS
// the product, no matter what accessory noun trails it ("Sofa And Toss
// Pillows"). Without this tier the "head noun is last" heuristic below picks
// "Pillows" and a $5,591 sofa renders as a pillow.
const PRIMARY_TYPES = new Set(
  [
    "Sectional Sofa", "Sleeper Sofa", "Loveseat Sofa", "Convertible Sofa", "Outdoor Sofa",
    "Sectional", "Sofa", "Loveseat", "Couch", "Chaise", "Settee", "Daybed",
    "Bed", "Bed Frame", "Platform Bed", "Bunk Bed",
  ].map((t) => t.toLowerCase())
);

// Accessory nouns that are only ever the product type when nothing primary is
// present in the title.
const ACCESSORY_TYPES = new Set(
  [
    "Pillow", "Throw Pillow", "Cushion", "Ottoman", "Storage Ottoman", "Ottoman Bench", "Throw", "Blanket",
    // "Bathroom Vanity And Medicine Cabinet" is a vanity.
    "Medicine Cabinet",
  ].map((t) => t.toLowerCase())
);

function typeRank(type: string): number {
  const t = type.toLowerCase();
  if (PRIMARY_TYPES.has(t)) return 2;
  if (ACCESSORY_TYPES.has(t)) return 0;
  return 1;
}

// What joins an add-on to the product ("Sofa & Toss Pillows", "Bench In Natural
// Finish"): the noun standing before one of these is still the product.
const ADD_ON_WORDS = /^(?:&|and|or|plus|in|for|on)$/i;
// A noun AFTER one of these is what the product is for or in ("Cover For Sofa").
const PREPOSITIONS = /^(?:in|for|on)$/i;
const CLAUSE_WORDS = /^(?:with|featuring|including)$/i;
const COUNT_WORDS = /^(?:sets?|pairs?|packs?|collection)$/i;

/** Every word of the supplier's product type, singular, plus each adjacent pair closed up ("love seats" → loveseat). */
function typeWords(productType?: string): Set<string> {
  const words = (productType || "").split(/[^A-Za-z]+/).filter(Boolean).map((w) => nounStem(w));
  const out = new Set(words.filter((w) => w.length > 2));
  for (let i = 1; i < words.length; i++) out.add(nounStem(words[i - 1] + words[i]));
  return out;
}

interface FoundType {
  type: string;
  startIdx: number;
  /** Title words the type covers, so they are not read again as descriptors. */
  wordCount: number;
  /** May take an "s" on a set ("2-Piece Bar Chairs"). */
  plural: boolean;
}

/**
 * The product's type noun, or null when the title does not make it plain.
 *
 * A noun the vocabulary knows is only the product when it is the HEAD of the
 * product phrase: the phrase ends on it, or an add-on follows ("Sofa & Toss
 * Pillows"). "Lamp" in "Lamp Shade", "Rug" in "Rug Pad", "Shelf" in "Shelf Life
 * Too 1 Framed Print Wall Art" are modifiers — picking them sold a shade as a
 * lamp and a rug pad as a rug. The supplier's product type breaks ties and
 * supplies the modifier the vocabulary lacks ("Bar" Chair, "Dog" Bed).
 * Returning null is always safe: the caller then shows the supplier's own title.
 */
function findProductType(words: string[], productType?: string): FoundType | null {
  const lower = words.map((w) => w.toLowerCase().replace(/[^a-z-]/g, ""));
  const supplier = typeWords(productType);
  const inSupplierType = (w: string) => supplier.has(nounStem(w));

  // The product phrase stops where an accessory clause opens: a noun inside
  // "With Two Drawers And Shelf" is a part. A trailing "Set" / "2 Pack" is a count.
  let end = words.findIndex((w, i) => i > 0 && (CLAUSE_WORDS.test(w) || /,$/.test(words[i - 1])));
  if (end < 0) end = words.length;
  while (end > 0 && (!lower[end - 1] || COUNT_WORDS.test(lower[end - 1]))) end--;

  interface Match { type: string; start: number; len: number; rank: number; own: boolean; isHead: boolean }
  const matches: Match[] = [];
  for (const type of PRODUCT_TYPES) {
    const parts = type.toLowerCase().split(" ");
    for (let i = end - parts.length; i >= 0; i--) {
      // Supplier titles pluralise the head noun on sets ("Table Lamps"), which
      // must still resolve to the "Table Lamp" type.
      if (!parts.every((p, j) => lower[i + j] === p || lower[i + j] === `${p}s` || lower[i + j] === `${p}es`)) continue;
      if (words.slice(0, i).some((w) => PREPOSITIONS.test(w))) continue;
      const next = i + parts.length;
      matches.push({
        type: type === "Arm Chair" ? "Armchair" : type,
        start: i,
        len: parts.length,
        rank: typeRank(type),
        own: parts.some(inSupplierType),
        isHead: next >= end || ADD_ON_WORDS.test(words[next]),
      });
    }
  }

  // Best head first: the supplier's own noun, then the priority tiers (a sofa
  // outranks its toss pillows), then the earlier noun ("Mirror & Stool" is a
  // mirror) and the longer match ("Storage Bench" over "Bench").
  const heads = matches
    .filter((m) => m.isHead)
    // A noun inside a longer match is that match's own word: "Cabinet" must not
    // outrank the "Medicine Cabinet" it belongs to (an accessory tier, so that a
    // vanity sold with one stays a vanity).
    .filter((m) => !matches.some((o) => o.isHead && o.len > m.len && o.start <= m.start && o.start + o.len >= m.start + m.len))
    // A sofa or a bed the supplier files under something else is not one
    // ("Raised Garden Bed" is a planter).
    .filter((m) => m.own || m.rank < 2 || supplier.size === 0)
    .sort((x, y) => Number(y.own) - Number(x.own) || y.rank - x.rank || x.start - y.start || y.len - x.len);
  const head = heads[0];
  if (head) {
    // The modifier before the noun is part of the type when the supplier's type
    // says so: "Bar" Chair, "Shower" Bench, "Fire Pit" Table, "Dog" Bed.
    let start = head.start;
    for (let n = 0; n < 2 && start > 0; n++) {
      const isWord = (i: number) => /^[A-Za-z]+$/.test(words[i]); // never a count ("4-Light")
      if (start > 1 && isWord(start - 2) && isWord(start - 1) && inSupplierType(words[start - 2] + words[start - 1])) start -= 2;
      else if (isWord(start - 1) && inSupplierType(words[start - 1]) && !head.type.toLowerCase().split(" ").includes(lower[start - 1])) start -= 1;
      else break;
    }
    const prefix = words.slice(start, head.start).map((w) => titleCase(w));
    return {
      type: [...prefix, head.type].join(" "),
      startIdx: start,
      wordCount: head.start - start + head.len,
      plural: PLURAL_TYPES.has(head.type),
    };
  }

  // No head. A known noun followed by one or two more words is a compound whose
  // head the vocabulary lacks ("Planter Box", "Pillow Cover", "Mattress Topper"):
  // keep it whole — when the supplier's type confirms the noun.
  const stem = matches
    .filter((m) => m.own || supplier.size === 0)
    .sort((x, y) => y.start + y.len - (x.start + x.len) || y.len - x.len)[0];
  if (stem) {
    const tail = words.slice(stem.start + stem.len, end);
    if (tail.length <= 2 && !tail.some((w) => ADD_ON_WORDS.test(w))) {
      return {
        type: [stem.type, ...tail.map((w) => titleCase(w))].join(" "),
        startIdx: stem.start,
        wordCount: stem.len + tail.length,
        plural: false,
      };
    }
  }
  return null;
}



/** Spec-dump markers that supplier feeds append to prose. */
const SPEC_MARKERS = [
  "Details GTIN",
  "GTIN",
  "UPC",
  "EAN",
  "MPN",
  "Shipping Weight",
  "Shipping Size",
  "Shipping Dimensions",
  "Product Weight",
];

/** Strip vendor names / spec noise from product-facing copy. */
export function cleanProductDescription(text: string): string {
  if (!text) return text;
  let out = text
    .replace(/\s*\(\s*(HomeRoots|HomeSullivan|Furniture of America|Baxton Studio|Modway|Flash Furniture|FurniFindUSA|Furnifind|FurniFind)\s*\)\s*/gi, " ")
    .replace(VENDORS, "");

  // Drop trailing spec blocks wherever they start — not only past a char cut.
  let cutAt = -1;
  for (const marker of SPEC_MARKERS) {
    const idx = out.search(new RegExp(`(^|[\\s.;:•|-])${marker}\\b`, "i"));
    if (idx > 0 && (cutAt === -1 || idx < cutAt)) cutAt = idx;
  }
  if (cutAt > 0) out = out.substring(0, cutAt);

  return out
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.!?])/g, "$1")
    .trim();
}

/**
 * Truncate to `max` characters at a sentence boundary when possible, else at a
 * word boundary. Never cuts mid-word.
 */
export function truncateAtSentence(text: string, max = 200): string {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  const window = t.slice(0, max + 1);
  const sentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf("! "),
    window.lastIndexOf("? "),
  );
  if (sentenceEnd > max * 0.4) return window.slice(0, sentenceEnd + 1).trim();
  return window.replace(/\s+\S*$/, "").trim() + "…";
}


export function basicClean(title: string, { filler = true }: { filler?: boolean } = {}): string {
  // The trailing supplier spec block (`5.0" (L) x 18.0" (W) x 18.0" (H)`) is noise
  // in a name, and it hid the head noun from findProductType.
  return collapseStutters(stripSpecBlock(stripChemicalNames(title)))
    .replace(VENDORS, "")
    // leading and mid-title WxDxH dimensions like 98" X 43" X 30" (before single dims)
    .replace(/\d+(\.\d+)?\s*["']?\s*[Xx×]\s*\d+(\.\d+)?\s*["']?\s*[Xx×]\s*\d+(\.\d+)?\s*["']?/g, "")
    // leading WxD pairs like 60" X 30"
    .replace(/^\s*\d+(\.\d+)?\s*["']?\s*[Xx×]\s*\d+(\.\d+)?\s*["']?\s*/g, "")
    // leading standalone dimension like 27"
    .replace(/^\s*\d+(\.\d+)?"\s*/g, "")
    // stray empty parentheses left by vendor removal
    .replace(/\(\s*\)/g, "")
    .replace(/\bSet\s+of\s+(Two|2)\b/gi, "2 Piece")
    .replace(/\bSet\s+of\s+(Three|3)\b/gi, "3 Piece")
    .replace(/\bSet\s+of\s+(Four|4)\b/gi, "4 Piece")

    // catalog filler
    // "Novelty" is supplier taxonomy, not a descriptor — it reads as a defect in a
    // rendered name ("Bedside Novelty Lamp"). Stripped from display only; search
    // still matches on the raw Shopify title.
    // The other words are filler only in a recomposed name. `filler: false` keeps
    // them for a supplier title shown whole, where they are part of a phrase
    // ("Green Green Grass of Home", "Standard Size Medium Pillow").
    .replace(filler ? /\b(Standard|Traditional Style|Home|Furniture|Novelty)\b/gi : /\bNovelty\b/gi, "")
    .replace(/\bAnd\b/gi, "&")
    // normalise ALL-CAPS words, but preserve known acronyms
    .replace(/\b([A-Z]{3,})\b/g, (_m, w: string) =>
      ACRONYMS.has(w) ? w : w.charAt(0) + w.slice(1).toLowerCase()
    )
    // duplicate adjacent words: handled by collapseStutters above, which only
    // collapses doubled product nouns and never a colour that closes one phrase
    // and opens the next ("Green and Red Red Truck").
    .replace(/\s{2,}/g, " ")
    .replace(/\s*[-–—]\s*$/, "")
    .replace(/^\s*[-–—]\s*/, "")
    .replace(/^[,&]\s*/, "")
    .trim();
}

export interface TitleAttributes {
  color: string | null;
  material: string | null;
  functional: string | null;
  pieceCount: string | null;
  configs: string[];
  feature: string | null;
  type: string | null;
  /** The type may take an "s" on a set. */
  typePlural: boolean;
  /** Lighting/decor descriptors: shade shape, form, placement. */
  descriptors: string[];
  /** Normalised bulb count, e.g. "3-Light". */
  lightCount: string | null;
  /** Functional tech features present in the raw title, e.g. "LED", "USB". */
  techFeatures: string[];
}


/**
 * Parse the differentiating attributes out of a supplier title. `productType`
 * (the supplier's) is only used to settle which noun is the product.
 */
export function parseTitleAttributes(rawTitle: string, productType?: string): TitleAttributes {
  // A set of five or more and an "N Pack" are counts too: "Set of Five … Mirror
  // Set" at $361.99 read "Gold Metal Mirror". Only here, for the recomposed
  // name; a supplier title shown whole keeps its own "Set of Five".
  let rest = basicClean(rawTitle || "")
    .replace(
      /\bSet\s+of\s+(Five|Six|Seven|Eight|Nine|Ten|Eleven|Twelve|\d{1,2})\b/gi,
      (_m, n: string) => `${NUM_WORDS[n.toLowerCase()] ?? n} Piece`,
    )
    .replace(/\b(\d{1,2})\s*Pack\b/gi, "$1 Piece");

  const take = (re: RegExp, from = 0): RegExpMatchArray | null => {
    const m = rest.slice(from).match(re);
    if (m && m.index !== undefined) {
      const at = from + m.index;
      rest = (rest.slice(0, at) + " " + rest.slice(at + m[0].length))
        .replace(/\s{2,}/g, " ")
        .trim();
    }
    return m;
  };

  // Trailing feature phrase: "with Console", "with Toss Pillows",
  // "with Brown & White Empire Shade" — leading colour words are stripped (the
  // colour is already stated up front), but never so far that the phrase loses
  // its own descriptive word.
  const featM = take(/\s+with\s+((?:[A-Za-z][A-Za-z-]*|&)(?:\s+(?:[A-Za-z][A-Za-z-]*|&)){0,4})\s*$/i);
  let feature: string | null = null;
  if (featM) {
    const words = featM[1].trim().split(/\s+/);
    // "With Ceramic Sink With Two Drawers": the phrase stops where the next clause opens.
    const inner = words.findIndex((w, i) => i > 0 && /^with$/i.test(w));
    if (inner > 0) words.length = inner;
    const colorWord = new RegExp(`^(?:${COLORS}|${COLOR_PREFIXES}|Off|Antiqued|Shiny|Satin)$`, "i");
    while (words.filter((w) => w !== "&").length > 2 && (colorWord.test(words[0]) || words[0] === "&")) words.shift();
    while (words[0] === "&") words.shift();
    // Keep the whole phrase when it ends in a head noun like "Shade" — cutting
    // it at three words leaves an unfinished phrase ("with Clear Acrylic Drum").
    const endsInHeadNoun = /^(shades?|shelves|shelf|drawers?|tables?|pillows?|cushions?|bases?)$/i.test(
      words[words.length - 1] || ""
    );
    // Three words at most, not counting the "&" that joins them ("Shelf & Drawer").
    const kept: string[] = [];
    const whole = endsInHeadNoun && words.filter((w) => w !== "&").length <= 4;
    for (const w of words) {
      if (!whole && w !== "&" && kept.filter((k) => k !== "&").length >= 3) break;
      kept.push(w);
    }
    // Cut short, the half after an "&" is unfinished ("USB Port & Integrated"):
    // the phrase ends before it.
    if (kept.length < words.length && kept.includes("&")) kept.length = kept.lastIndexOf("&");
    while (kept.length && kept[kept.length - 1] === "&") kept.pop();
    const phrase = kept.join(" ");
    if (phrase) feature = `with ${titleCase(phrase)}`;
  }
  // A vanity sold with its sink says so, whatever else the title lists after it.
  if (
    !/\bsinks?\b/i.test(feature ?? "") &&
    /\bwith\b[^,]*\bsinks?\b/i.test(rest + (featM ? ` ${featM[0]}` : "")) &&
    !/\b(?:no|not|without|excluded|excluding|optional|separately)\b/i.test(rawTitle || "")
  ) {
    feature = "with Sink";
  }




  // Piece count → digits
  const pcM = take(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|\d{1,2})[\s-]*(?:piece|pieces|pc|pcs)\b/i);
  let pieceCount: string | null = null;
  if (pcM) {
    const token = pcM[1].toLowerCase();
    const n = NUM_WORDS[token] ?? parseInt(token, 10);
    if (n && n > 1) pieceCount = `${n}-Piece`;
  }

  // A colour or material named only inside the accessory clause belongs to a
  // part: "Natural Wood Vanity With White Sink" is not a white vanity. What a
  // store-written title puts behind its own dash is the product's again:
  // "Modular Sofa with Toss Pillows — Black Linen Blend".
  const takeFromProduct = (re: RegExp): RegExpMatchArray | null => {
    const clause = rest.search(/,|\s(?:with|featuring|including)\s/i);
    if (clause < 0 || re.test(rest.slice(0, clause))) return take(re);
    const dash = rest.indexOf(" \u2014 ", clause);
    return dash >= 0 ? take(re, dash) : null;
  };

  // "2-Light Gray Sconce" and "Four Light Bronze Wall Sconce" state a bulb count,
  // not the colour "Light Gray" / "Light Bronze": close the phrase up before the
  // colour is read (the bulb-count parser below still reads "2-Lights"). Only
  // where a light fitting follows: "Two Light Gray Chairs" are light gray.
  rest = rest.replace(
    /\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|\d{1,2})[\s-]*lights?\b(?=\s+[A-Za-z].*\b(?:lights?|lamps?|sconces?|chandeliers?|pendants?|lanterns?|fixtures?|candle\s+holders?)\b)/i,
    "$1-Lights",
  );

  // Colour — prefer the leading colour phrase, else the first colour anywhere.
  let color: string | null = null;
  const leadColor = take(LEADING_COLOR_RE);
  if (leadColor) {
    color = titleCase(leadColor[1].trim()).replace(/\s*&\s*/g, " & ");
  } else {
    const anyColor = takeFromProduct(ANY_COLOR_RE);
    if (anyColor) color = titleCase(anyColor[1].trim());
  }

  // Material (with qualifier)
  const matM = takeFromProduct(PCT_MATERIAL_RE) ?? takeFromProduct(MATERIAL_BLEND_RE);
  let material: string | null = null;
  if (matM) {
    const qualifier = matM[1] ? titleCase(matM[1].replace(/\s+/g, "-")) + " " : "";
    // "Silk Blend" is not silk and "Leather Match" is not leather: the word stays.
    material = qualifier + titleCase(matM[2]) + (matM[3] ? " " + titleCase(matM[3]) : "");
  }

  // Functional modifier (with Power/Manual qualifier)
  const funcM = take(FUNCTIONAL_RE);
  let functional: string | null = null;
  if (funcM) {
    const qualifier = funcM[1] ? titleCase(funcM[1]) + " " : "";
    functional = qualifier + titleCase(funcM[2] === "Recliner" ? "Reclining" : funcM[2]);
  }

  // Configuration words — keep the top-priority one, plus Chaise as a secondary.
  const configs: string[] = [];
  for (const [re, label] of CONFIGS) {
    if (re.test(rest)) {
      const primaryTaken = configs.length > 0;
      if (!primaryTaken || label === "Chaise") {
        configs.push(label);
      }
      take(re);
    }
  }

  const words = rest.split(/\s+/).filter(Boolean);
  const found = findProductType(words, productType);

  // Lighting/decor descriptors — parsed from what's left AFTER the head noun is
  // removed, so a real "Desk" or "Buffet" product keeps its type.
  let restNoType = rest;
  if (found) {
    restNoType = words
      .filter((_w, i) => i < found.startIdx || i >= found.startIdx + found.wordCount)
      .join(" ");
  }

  const descriptors: string[] = [];
  for (const [re, label] of LIGHT_DESCRIPTORS) {
    if (descriptors.length >= 2) break;
    if (re.test(restNoType)) descriptors.push(label);
  }

  // Leading light/bulb count: "3-Light", "Six Bulb"
  let lightCount: string | null = null;
  const lcM = rest.match(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|\d{1,2})[\s-]*(?:light|lights|bulb|bulbs)\b/i);
  if (lcM) {
    const token = lcM[1].toLowerCase();
    const n = NUM_WORDS[token] ?? parseInt(token, 10);
    if (n && n > 1) lightCount = `${n}-Light`;
  }

  // Tech features — kept only when the RAW title contains them, never invented.
  const techFeatures: string[] = [];
  for (const [re, label] of TECH_FEATURES) {
    if (re.test(rawTitle || "")) techFeatures.push(label);
  }

  return {
    color,
    material,
    functional,
    pieceCount,
    configs,
    feature,
    type: found?.type ?? null,
    typePlural: found?.plural ?? false,
    descriptors,
    lightCount,
    techFeatures,
  };
}


const MAX_LEN = 85;

function truncate(t: string): string {
  if (t.length <= MAX_LEN) return t;
  const breakPattern = /\s+(?:with|in|for|from|by|&|and)\s+/gi;
  let lastBreak = -1;
  let match: RegExpExecArray | null;
  while ((match = breakPattern.exec(t)) !== null) {
    if (match.index <= MAX_LEN - 5 && match.index > lastBreak) lastBreak = match.index;
  }
  if (lastBreak > 25) return t.substring(0, lastBreak).trim();
  return t.substring(0, MAX_LEN - 3).replace(/\s+\S*$/, "").trim();
}

/**
 * The supplier's own (cleaned) title, for when no shorter name is safe. Cut
 * from the END a long one loses its noun ("… Black Framed Print" for a "… Black
 * Framed Print Wall Art"), so past the cap the accessory clause goes first and
 * then the words just before the noun — never the noun.
 */
function supplierName(cleaned: string, productType?: string): string {
  if (cleaned.length <= MAX_LEN) return cleaned;
  const kept = productPhrase(cleaned, productType).split(/\s+/);
  let at = kept.length - 3; // the last two words are the noun phrase
  for (; at >= 1 && kept.join(" ").length > MAX_LEN; at--) kept.splice(at, 1);
  // What now stands before the noun must not dangle: no "&", no list comma.
  if (at >= 1 && /^(?:&|and|or|with)$/i.test(kept[at])) kept.splice(at--, 1);
  if (at >= 0 && at < kept.length - 2) kept[at] = kept[at].replace(/,$/, "");
  return truncate(kept.join(" ").replace(/,$/, ""));
}

/**
 * Boutique display title:
 * [Colour] [Material] [Functional] [PieceCount] [Configuration] [Type] [with Feature]
 *
 * Pass the supplier's `productType` wherever it is known. The recomposed name
 * is only used when it still calls the product what the supplier calls it
 * (displayNameDefects); otherwise the cleaned supplier title is shown as is.
 */
export function cleanProductTitle(title: string, productType?: string): string {
  if (!title) return "";
  const cleaned = basicClean(title);
  const a = parseTitleAttributes(title, productType);

  // Rugs are chosen by size, and one design ships in up to nine sizes: a card
  // name without it ("Walnut & Gold Outdoor Area Rug") hid the one fact a
  // shopper searching "rug 8x10" needs.
  const sized = (name: string, type: string) => {
    if (!/\b(rugs?|runners?)\b/i.test(type)) return name;
    const size = extractSizeLabel(title);
    return size && /\d/.test(size) && !name.toLowerCase().includes(size.toLowerCase()) ? `${size} ${name}` : name;
  };
  // Beds, bunks, headboards and mattresses are chosen by size too.
  const bedSize = (type: string): string | null => {
    if (!/\b(?:beds?|bed frames?|headboards?|daybeds?|mattress(?:es)?)\b/i.test(type)) return null;
    const one = "(?:Split\\s+)?(?:California\\s+King|Twin\\s+XL|Twin|Full|Queen|King)";
    const m = title.match(new RegExp(`\\b${one}(?:\\s+Over\\s+${one})?\\b`, "i"));
    return m ? titleCase(m[0].replace(/\s+/g, " ")).replace(/\bXl\b/g, "XL") : null;
  };
  // The supplier's own title, when no recomposed name is safe.
  const fallback = () => {
    const whole = basicClean(title, { filler: false });
    return sized(supplierName(whole, productType), `${a.type ?? ""} ${productType ?? ""} ${productPhrase(whole, productType)}`);
  };

  if (!a.type) return fallback();

  const isSet = !!a.pieceCount || /\b(Set\s+of|Pair\s+of)\b/i.test(title);
  let type = a.type;
  if (isSet && a.typePlural && !type.endsWith("s")) type += "s";

  // Style descriptor is optional colour/material-adjacent flavour.
  let descriptor: string | null = null;
  const lowerTitle = cleaned.toLowerCase();
  for (const [key, label] of Object.entries(STYLE_DESCRIPTORS)) {
    if (new RegExp(`\\b${key}\\b`).test(lowerTitle)) { descriptor = label; break; }
  }
  if (descriptor && a.material && descriptor.toLowerCase() === a.material.toLowerCase()) descriptor = null;

  const lowerFeature = (a.feature || "").toLowerCase();
  const lowerType = type.toLowerCase();
  const lightDescriptors = a.descriptors.filter(
    (d) => !lowerFeature.includes(d.toLowerCase()) && !new RegExp(`\\b${d.toLowerCase()}\\b`).test(lowerType)
  );


  const techFeatures = a.techFeatures.filter(
    (f) =>
      !new RegExp(`\\b${f}\\b`, "i").test(lowerType) &&
      !new RegExp(`\\b${f}\\b`, "i").test(lowerFeature)
  );

  const parts = [
    a.color,
    descriptor,
    a.material,
    ...techFeatures,
    a.functional,
    a.pieceCount,
    a.lightCount,
    ...lightDescriptors,
    ...a.configs.filter((c) => c.toLowerCase() !== type.toLowerCase()),
    type,
    a.feature,
  ].filter(Boolean) as string[];


  const out: string[] = [];
  for (const p of parts) {
    if (out.length === 0 || out[out.length - 1].toLowerCase() !== p.toLowerCase()) out.push(p);
  }
  // Nothing but the type survived ("Mattress", "Bed Pillow"): that names a
  // category, not this product.
  if (out.length === 1) return fallback();
  // The bed size goes in front of the type ("Brown King Bed Frame"). Added after
  // the check above: a size and a type alone are still only a category.
  const bed = bedSize(type);
  if (bed && !out.some((p) => p.toLowerCase().includes(bed.toLowerCase()))) out.splice(out.indexOf(type), 0, bed);

  const result = out.join(" ").replace(/\s{2,}/g, " ").trim();
  if (result.length < 4) return fallback();
  const name = truncate(sized(result, type));
  // The name must still say what the product is. When the recomposition lost the
  // supplier's noun, the supplier's own title is the honest name.
  const lost = displayNameDefects(cleaned, productType, name);
  return lost.typeNoun || lost.headNoun ? fallback() : name;
}

/* ------------------------------------------------------------------ */
/* Uniqueness guard                                                    */
/* ------------------------------------------------------------------ */

interface MinimalProduct {
  node: {
    id?: string;
    handle: string;
    title: string;
    productType?: string;
    variants?: { edges?: Array<{ node: { title?: string } }> };
    priceRange?: { minVariantPrice?: { amount?: string } };
    images?: { edges?: Array<{ node: { url?: string } }> };
  };
}

const priceOf = (p: MinimalProduct) => p.node.priceRange?.minVariantPrice?.amount || "";

// Size/measure extraction and the qualifier vocabularies come from the shared
// rules module; re-exported here because the storefront imports them from this file.
export { extractSizeLabel, extractSingleMeasure };




/** Signature of the attributes that could legitimately distinguish two SKUs. */
function distinguishingSignature(p: MinimalProduct): string {
  const a = parseTitleAttributes(p.node.title);
  const variantTitle = p.node.variants?.edges?.[0]?.node?.title;
  return [
    a.color,
    a.material,
    a.functional,
    a.pieceCount,
    a.configs.join("+"),
    a.feature,
    variantTitle && variantTitle !== "Default Title" ? variantTitle : "",
  ]
    .map((v) => (v || "").toLowerCase())
    .join("|");
}

/** A photo's file, without the query string and the upload id the supplier's daily re-uploads change. */
const leadImage = (p: MinimalProduct) =>
  (p.node.images?.edges?.[0]?.node?.url || "")
    .split("?")[0]
    .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.\w+$)/i, "");

/**
 * Remove genuine feed duplicates:
 *  - repeated ids/handles,
 *  - a handle ending in `-1` whose title exactly matches another product,
 *  - products that resolve to the same display name with no distinguishing
 *    attribute AND the same price (collapse, never number them).
 *
 * That is the rule for a rail, which must not repeat itself. A `listing`
 * (category grid, search results) is where every product has to be reachable,
 * and there the two title rules hid different products: other bed sizes, other
 * rug patterns, 577 of 861 chandeliers. A listing drops only a product the
 * shopper could not tell from one already shown: the same supplier title, the
 * same price and the same lead photo.
 */
export function dedupeProducts<T extends MinimalProduct>(products: T[], { listing = false }: { listing?: boolean } = {}): T[] {
  const seenHandles = new Set<string>();
  const seenIds = new Set<string>();
  const titles = new Map<string, string>(); // normalised title → handle

  for (const p of products) {
    const norm = (p.node.title || "").trim().toLowerCase();
    if (!titles.has(norm)) titles.set(norm, p.node.handle);
  }

  const out: T[] = [];
  const collapseKeys = new Set<string>();

  for (const p of products) {
    const { handle, id, title } = p.node;
    if (seenHandles.has(handle)) continue;
    if (id && seenIds.has(id)) continue;
    const norm = (title || "").trim().toLowerCase();
    if (!listing && /-\d+$/.test(handle) && titles.get(norm) && titles.get(norm) !== handle) continue;

    // Rails: same display name + same attributes + same price → collapse. A
    // differing price keeps both (and the display-name helper then appends the
    // price instead of a number).
    const key = listing
      ? [norm, priceOf(p), leadImage(p)].join("::")
      : [cleanProductTitle(title, p.node.productType).toLowerCase(), distinguishingSignature(p), priceOf(p)].join("::");
    if (collapseKeys.has(key)) continue;
    collapseKeys.add(key);

    seenHandles.add(handle);
    if (id) seenIds.add(id);
    out.push(p);
  }
  return out;
}







/**
 * Build handle → display name, guaranteeing no two products in the list share
 * a name. Appends the next distinguishing attribute (piece count, colour,
 * variant/size) until the names differ.
 */
export function uniqueDisplayNames<T extends MinimalProduct>(products: T[]): Record<string, string> {
  const base = new Map<string, string>();
  const groups = new Map<string, T[]>();

  for (const p of products) {
    const name = cleanProductTitle(p.node.title, p.node.productType);
    base.set(p.node.handle, name);
    const key = name.toLowerCase();
    const arr = groups.get(key) || [];
    arr.push(p);
    groups.set(key, arr);
  }

  const result: Record<string, string> = {};
  for (const [, group] of groups) {
    if (group.length === 1) {
      const p = group[0];
      result[p.node.handle] = base.get(p.node.handle)!;
      continue;
    }

    const used = new Set<string>();
    for (const p of group) {
      const name = base.get(p.node.handle)!;
      if (!used.has(name.toLowerCase())) {
        used.add(name.toLowerCase());
        result[p.node.handle] = name;
        continue;
      }

      const attrs = parseTitleAttributes(p.node.title);
      const variantTitle = p.node.variants?.edges?.[0]?.node?.title;
      const realVariant = variantTitle && variantTitle !== "Default Title" ? variantTitle : null;
      // Round 42: handle-derived qualifiers must be backed by the raw title or
      // the product type — supplier handles are stale (`…-faux-fur-mirror-…` on
      // a brass mirror), and an unsupported claim must never reach a shopper.
      const fromHandle = handleQualifiers(p.node.handle, name, p.node.title, p.node.productType);
      // When the title states a full dimension pair (7" x 7"), a lone measure
      // ("7"") is a fragment of it and only confuses the shopper.
      const titleHasPair = /\d\s*(?:"|''|in\.?|inch(?:es)?|'|ft\.?|feet|cm)?\s*[xX×]\s*\d/.test(p.node.title || "");

      // Exactly ONE qualifier, chosen best-first: size → pattern/style/finish
      // descriptor → colour → material. Never stacked.
      const ordered: (string | null)[] = [
        // 1. size / dimension
        extractSizeLabel(p.node.title),
        titleHasPair ? null : extractSingleMeasure(p.node.title),
        realVariant ? extractSizeLabel(realVariant) : null,
        realVariant && !titleHasPair ? extractSingleMeasure(realVariant) : null,
        ...(titleHasPair ? [] : fromHandle.sizes),

        // 2. pattern / style / finish descriptor
        attrs.descriptors[0] || null,
        attrs.configs[0] || null,
        attrs.lightCount,
        attrs.feature,
        ...fromHandle.descriptors,
        // 3. colour
        attrs.color,
        ...fromHandle.colors,
        // 4. material
        attrs.material,
        ...fromHandle.materials,
      ];

      let chosen: string | null = null;
      for (const raw of ordered) {
        if (!raw) continue;
        const q = titleCase(raw.trim());
        if (!isUsableQualifier(name, q, p.node.title)) continue;
        const candidate = `${name} — ${q}`;
        if (used.has(candidate.toLowerCase())) continue;
        chosen = candidate;
        break;
      }

      // Fall back to the price. If the price collides too, leave the name
      // unchanged — two indistinguishable rows beat a nonsense suffix, and
      // dedupeProducts already collapses true duplicates upstream.
      if (!chosen) {
        const price = priceOf(p);
        const priceNum = price ? parseFloat(price) : NaN;
        if (Number.isFinite(priceNum) && priceNum > 0) {
          const label = `${name} — $${Math.round(priceNum).toLocaleString("en-US")}`;
          if (!used.has(label.toLowerCase())) chosen = label;
        }
      }

      const final = chosen || name;
      used.add(final.toLowerCase());
      result[p.node.handle] = final;
    }
  }


  return result;
}
